import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/hooks/useCurrentUser";

export interface AdminApplication {
  id: number;
  user_id: string | null;
  applicant_name: string;
  email: string;
  company: string | null;
  product_name: string;
  product_version: string | null;
  edition: string;
  seat_count: number;
  machine_code: string | null;
  use_case: string | null;
  status: string;
  reviewer_note: string | null;
  reviewed_at: string | null;
  created_at: string;
  renew_license_id: number | null;
}

export interface AdminLicense {
  id: number;
  application_id: number | null;
  user_id: string | null;
  license_key: string;
  product_name: string;
  edition: string;
  status: string;
  seat_count: number;
  activation_count: number;
  max_activations: number;
  max_opens: number | null;
  open_count: number;
  last_validated_at: string | null;
  issued_at: string | null;
  expires_at: string | null;
  created_at: string;
}

interface ListResponse {
  applications: AdminApplication[];
  licenses: AdminLicense[];
}

interface ActionResult {
  ok?: boolean;
  email?: { sent: boolean; reason?: string };
}

export interface BatchResult {
  processed: number;
  skipped: number;
  emailSent: number;
  emailFailed: number;
}

export interface BatchRenewResult {
  processed: number;
  skipped: number;
}

export interface RenewInput {
  expiresMonths?: number;
  expiresAt?: string;
}

async function invokeAdmin<T>(payload: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke("admin-license", { body: payload });
  if (error) {
    let message = error.message || "操作失败，请稍后重试";
    try {
      const context = (error as { context?: Response }).context;
      if (context) {
        const parsed = await context.json();
        if (parsed?.error) message = parsed.error;
      }
    } catch {
      // keep original message
    }
    throw new Error(message);
  }
  const result = data as { error?: string } | null;
  if (result?.error) throw new Error(result.error);
  return data as T;
}

interface UseAdminDataResult {
  applications: AdminApplication[];
  licenses: AdminLicense[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  approve: (applicationId: number, reviewerNote: string, expiresMonths: number) => Promise<ActionResult>;
  reject: (applicationId: number, reviewerNote: string) => Promise<ActionResult>;
  approveMany: (applicationIds: number[], reviewerNote: string, expiresMonths: number) => Promise<BatchResult>;
  rejectMany: (applicationIds: number[], reviewerNote: string) => Promise<BatchResult>;
  renew: (licenseId: number, input: RenewInput) => Promise<{ expiresAt: string }>;
  renewMany: (licenseIds: number[], expiresMonths: number) => Promise<BatchRenewResult>;
  revoke: (licenseId: number) => Promise<void>;
  restore: (licenseId: number) => Promise<void>;
  setOpenLimit: (licenseId: number, maxOpens: number | null, resetCount?: boolean) => Promise<void>;
}

export function useAdminData(): UseAdminDataResult {
  const { user, loading: authLoading } = useCurrentUser();
  const [applications, setApplications] = useState<AdminApplication[]>([]);
  const [licenses, setLicenses] = useState<AdminLicense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setApplications([]);
      setLicenses([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await invokeAdmin<ListResponse>({ action: "list" });
      setApplications(data.applications ?? []);
      setLicenses(data.licenses ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "管理数据加载失败");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const approve = useCallback(
    async (applicationId: number, reviewerNote: string, expiresMonths: number) => {
      const result = await invokeAdmin<ActionResult>({
        action: "approve",
        applicationId,
        reviewerNote,
        expiresMonths,
      });
      await load();
      return result;
    },
    [load],
  );

  const reject = useCallback(
    async (applicationId: number, reviewerNote: string) => {
      const result = await invokeAdmin<ActionResult>({ action: "reject", applicationId, reviewerNote });
      await load();
      return result;
    },
    [load],
  );

  const approveMany = useCallback(
    async (applicationIds: number[], reviewerNote: string, expiresMonths: number) => {
      const result = await invokeAdmin<BatchResult>({
        action: "approveMany",
        applicationIds,
        reviewerNote,
        expiresMonths,
      });
      await load();
      return result;
    },
    [load],
  );

  const rejectMany = useCallback(
    async (applicationIds: number[], reviewerNote: string) => {
      const result = await invokeAdmin<BatchResult>({
        action: "rejectMany",
        applicationIds,
        reviewerNote,
      });
      await load();
      return result;
    },
    [load],
  );

  const renew = useCallback(
    async (licenseId: number, input: RenewInput) => {
      const result = await invokeAdmin<{ expiresAt: string }>({
        action: "renew",
        licenseId,
        ...input,
      });
      await load();
      return result;
    },
    [load],
  );

  const renewMany = useCallback(
    async (licenseIds: number[], expiresMonths: number) => {
      const result = await invokeAdmin<BatchRenewResult>({
        action: "renewMany",
        licenseIds,
        expiresMonths,
      });
      await load();
      return result;
    },
    [load],
  );

  const revoke = useCallback(
    async (licenseId: number) => {
      await invokeAdmin({ action: "revoke", licenseId });
      await load();
    },
    [load],
  );

  const restore = useCallback(
    async (licenseId: number) => {
      await invokeAdmin({ action: "restore", licenseId });
      await load();
    },
    [load],
  );

  const setOpenLimit = useCallback(
    async (licenseId: number, maxOpens: number | null, resetCount = false) => {
      await invokeAdmin({ action: "setOpenLimit", licenseId, maxOpens, resetCount });
      await load();
    },
    [load],
  );

  return {
    applications,
    licenses,
    loading: loading || authLoading,
    error,
    reload: load,
    approve,
    reject,
    approveMany,
    rejectMany,
    renew,
    renewMany,
    revoke,
    restore,
    setOpenLimit,
  };
}